
type HtmlCharacters = "&" | "<" | ">" | '"' | "'" | "/";

/**
 * Escapes all HTML characters in a given string with safe versions.
 *
 * @param value The string to escape.
 * @param exclude A collection of characters to exclude from the selection.
 *
 * @return A string that contains escaped characters or undefined if the value is undefined.
 */
export function escapeHtml(value: string, exclude: HtmlCharacters[] = []): string {
    if (!value) {
        return undefined;
    }

    // tslint:disable:object-literal-sort-keys
    // It's easier ot read in this order so linter is wrong here.
    const htmlEscapes: { [key: string]: string } = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#x27;",
        "/": "&#x2F;"
    };
    // tslint:enable:object-literal-sort-keys

    // Just replace it with itself.
    for (const ex of exclude) {
        htmlEscapes[ex] = ex;
    }

    const htmlEscaper = /(&|<|>|"|'|\/)/g;
    return ("" + value).replace(htmlEscaper, match => {
        return htmlEscapes[match];
    });
}

/**
 * Does the opposite of escapeHtml;
 * @param value The escaped string that will be converted to HTML
 */
export function unescapeHtml(value: string): string {
    if (!value) {
        return undefined;
    }

    // tslint:disable:object-literal-sort-keys
    const htmlEscapes: { [key: string]: string } = {
        "&amp;": "&",
        "&lt;": "<",
        "&gt;": ">",
        "&quot;": '"',
        "&#x27;": "'",
        "&#x2F;": "/"
    };
    // tslint:enable:object-literal-sort-keys

    const htmlEscaper = /(&amp;|&lt;|&gt;|&quot;|&#x27;|&#x2F;)/g;
    return ("" + value).replace(htmlEscaper, match => {
        return htmlEscapes[match];
    });
}

/**
 * Turns a string into an ID that has dashes instead of spaces and is lowercase
 *
 *
 * @export
 * @param {string} value
 * @returns {string}
 */
export function idify(value: string): string {
    // lower case
    let id = value.toLowerCase();

    // and replace all spaces and replace with dashes
    id = id.replace(/ /g, "-");
    // then remove all remaining unsafe chars
    id = id.replace(/[^a-zA-Z0-9-_]/g, "");

    return id;
}

/**
 * Removes all characters from a string except alphabetical characters
 *
 * @export
 * @param {string} value
 * @returns {string}
 */
export function alphabetify(value: string): string {
    return value.replace(/[^A-Za-z]/g, "");
}

export interface ParsedUrl {
    href: string; // => "http:www.example.com:3000/pathname/#hash?search=test"
    protocol: string; // => "http:"
    host: string; // => "example.com:3000"
    hostname: string; // => "example.com"
    port: string; // => "3000"
    pathname: string; // => "pathname/"
    hash: string; // => "hash"
    search: string; // => "search=test"
}

/**
 * Parse a URL in to segments.  Each missing segment will be a simple empty string.
 * @param url The URl to parse.
 */
export function parseUrl(url = ""): ParsedUrl {
    const parsed = url.match(/^(https?\:)\/\/(([^:\/?#]*)(?:\:([0-9]+))?)([\/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/) || [];
    // tslint:disable:object-literal-sort-keys
    return {
        href: url || "",
        protocol: parsed[1] || "",
        host: parsed[2] || "",
        hostname: parsed[3] || "",
        port: parsed[4] || "",
        pathname: (parsed[5] || "").substr(1),
        search: (parsed[6] || "").substr(1),
        hash: (parsed[7] || "").substr(1)
    };
    // tslint:enable:object-literal-sort-keys
}

/**
 * Joins a series of strings and appends the last two with a conjunction.
 *
 * For example:
 *
 * let example = ["One", "Two", "Three"];
 * let joined = joinConj(example);
 * // joined === "One, Two, and Three";
 *
 * A single value will just return that value and two values will not include a comma.
 *
 * An undefined or blank values will result in an empty string.
 *
 * @param values The values to join.
 * @param conjunction The conjunction to include. Default is "and"
 */
export function joinConj(values: string[] = [], conjunction = "and") {
    if (values.length === 0) {
        return "";
    }
    if (values.length === 1) {
        return values[0];
    }
    if (values.length === 2) {
        return values[0] + " " + conjunction + " " + values[1];
    }

    const joined = values.join(", ");
    const lastIndexOfComma = joined.lastIndexOf(",");
    return joined.substring(0, lastIndexOfComma) + ", " + conjunction + joined.substring(lastIndexOfComma + 1);
}

/**
 * Tests if a string has any non whitespace content (emptyish)
 * @param stringToTest the string to determine whether it is emptyish or not
 */
export function isEmptyishString(stringToTest: string): boolean {
    // if the string is undefined
    if (typeof stringToTest === "undefined") {
        // consider it emptyish
        return true;
    }

    return stringToTest.replace(/\s/g, "").length === 0;
}

/**
 * Pads the string with zeros on the left to meet
 * either length 2 or the provided length.
 *
 * If a string that is over length of 2 or the provided length,
 * it will be trimmed.
 *
 * @param {string} str
 * @param {number} [len]
 * @returns
 */
export function padZero(str: string, len = 2): string {
    const zeros = new Array(len).join("0");
    return (zeros + str).slice(-len);
}

/**
 * Returns a positive number if a is larger alphabetically than b, 0 if the same, negative if
 * b is larger than a.
 *
 * Use within Array.prototype.sort function.
 *
 * @export
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function compareAlphabetical(a: string, b: string): number {
    const lowerA = a.toLowerCase();
    const lowerB = b.toLowerCase();

    if (lowerA > lowerB) {
        return 1;
    }

    if (lowerA < lowerB) {
        return -1;
    }

    return 0;
}

/**
 * Sorts the provided list in alphabetical order.
 *
 * @export
 * @param {string[]} unsorted
 * @returns {string[]}
 */
export function sortAlphabetical(unsorted: readonly string[]): readonly string[] {
    const copy = unsorted.slice();
    return copy.sort(compareAlphabetical);
}

/**
 * Sorts the provided list in alphabetical order.
 *
 * @export
 * @param {T[]} unsorted
 * @returns {T[]}
 */
export function sortAlpha<T>(unsorted: readonly T[], keySelector: (obj: T) => string): T[] {
    const copy = unsorted.slice();
    return copy.sort((a, b) => compareAlphabetical(keySelector(a), keySelector(b)));
}

/**
 * Determines if a string equals "true"
 *
 * @export
 * @param {string}
 * @returns {boolean}
 */
export function isTrue(boolString: string): boolean {
    try {
        return boolString.toLowerCase() === "true";
    } catch (e) {
        console.error("argument was not a string");
    }
}

/**
 * Returns the value of the specified key in a string in the format
 * <key1>=<value1>&<key2>=<value2>&<key3>=<value3> or /?<key1>=<value1>&<key2>=<value2>&<key3>=<value3>
 *
 * An array of values will be returned. On most occasions it will only be one value, but
 * on search parameters where there are multiple instances of the key, all values will be returned.
 *
 * @param key
 * @param search
 */
export function getValueOfKey(key: string, search = ""): string[] {
    const values: string[] = [];

    if (typeof search !== "string") {
        return [];
    }

    const split = search.slice(search.indexOf("?") < 0 ? 0 : search.indexOf("?") + 1).split("&");
    for (const s of split) {
        const equalsSplit = s.split("=");
        if (equalsSplit[0] === key) {
            values.push(equalsSplit[1]);
        }
    }
    return values;
}

/**
 * Allows adding multiple utterances at once by separating them by a comma (,) or new line (\n)
 */
export function splitByDelimiter(text: string): string[] {
    if (isEmptyishString(text)) {
        return [];
    }

    return text
        .split(/[\r\n,]+/)
        .filter(utterance => {
            if (!isEmptyishString(utterance)) {
                return utterance;
            }
        })
        .map(utterance => {
            return utterance.trim();
        });
}